import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { Layout } from '../components/Layout';

interface Coupon {
  id: number;
  code: string;
  name: string;
  description: string | null;
  coupon_type: string;
  discount_type: string;
  discount_value: number;
  max_discount_amount: number | null;
  min_order_amount: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface CouponsResponse {
  coupons: Coupon[];
  total: number;
  page: number;
  limit: number;
}

interface CouponFormData {
  code: string;
  name: string;
  description: string;
  coupon_type: string;
  discount_type: string;
  discount_value: string;
  max_discount_amount: string;
  min_order_amount: string;
}

export const CouponsPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [couponType, setCouponType] = useState('');
  const [isActive, setIsActive] = useState<boolean | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<CouponFormData>({
    code: '',
    name: '',
    description: '',
    coupon_type: 'payment_discount',
    discount_type: 'fixed',
    discount_value: '',
    max_discount_amount: '',
    min_order_amount: '',
  });

  const { data: couponsData, isLoading, error } = useQuery<CouponsResponse>({
    queryKey: ['coupons', page, limit, search, couponType, isActive],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('skip', ((page - 1) * limit).toString());
      params.append('limit', limit.toString());
      if (search) params.append('search', search);
      if (couponType) params.append('coupon_type', couponType);
      if (isActive !== null) params.append('is_active', isActive.toString());

      const response = await apiClient.get<CouponsResponse>(`/coupons?${params.toString()}`);
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiClient.post('/coupons', data);
      return response.data;
    },
    onSuccess: () => {
      alert('쿠폰이 생성되었습니다.');
      setShowCreateModal(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || '쿠폰 생성에 실패했습니다.';
      alert(errorMessage);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const response = await apiClient.patch(`/coupons/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      alert('쿠폰이 수정되었습니다.');
      setEditingCoupon(null);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || '쿠폰 수정에 실패했습니다.';
      alert(errorMessage);
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: number; is_active: boolean }) => {
      const response = await apiClient.patch(`/coupons/${id}`, { is_active });
      return response.data;
    },
    onSuccess: () => {
      alert('쿠폰 상태가 변경되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || '상태 변경에 실패했습니다.';
      alert(errorMessage);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/coupons/${id}`);
    },
    onSuccess: () => {
      alert('쿠폰이 삭제되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || '쿠폰 삭제에 실패했습니다.';
      alert(errorMessage);
    },
  });

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleReset = () => {
    setSearchInput('');
    setSearch('');
    setCouponType('');
    setIsActive(null);
    setPage(1);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      coupon_type: 'payment_discount',
      discount_type: 'fixed',
      discount_value: '',
      max_discount_amount: '',
      min_order_amount: '',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      code: formData.code,
      name: formData.name,
      description: formData.description || null,
      coupon_type: formData.coupon_type,
      discount_type: formData.discount_type,
      discount_value: parseFloat(formData.discount_value),
      max_discount_amount: formData.max_discount_amount ? parseInt(formData.max_discount_amount) : null,
      min_order_amount: formData.min_order_amount ? parseInt(formData.min_order_amount) : null,
    };

    if (editingCoupon) {
      updateMutation.mutate({ id: editingCoupon.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleEdit = (coupon: Coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      name: coupon.name,
      description: coupon.description || '',
      coupon_type: coupon.coupon_type,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_type === 'fixed'
        ? Math.floor(Number(coupon.discount_value)).toString()
        : coupon.discount_value.toString(),
      max_discount_amount: coupon.max_discount_amount !== null && coupon.max_discount_amount !== undefined
        ? Math.floor(Number(coupon.max_discount_amount)).toString()
        : '0',
      min_order_amount: coupon.min_order_amount !== null && coupon.min_order_amount !== undefined
        ? Math.floor(Number(coupon.min_order_amount)).toString()
        : '0',
    });
    setShowCreateModal(true);
  };

  const handleToggleActive = (coupon: Coupon) => {
    if (window.confirm(`쿠폰을 ${coupon.is_active ? '비활성화' : '활성화'} 하시겠습니까?`)) {
      toggleActiveMutation.mutate({ id: coupon.id, is_active: !coupon.is_active });
    }
  };

  const handleDelete = (coupon: Coupon) => {
    if (window.confirm(`정말로 "${coupon.name}" 쿠폰을 삭제하시겠습니까?\n사용자에게 발급되었거나 프로모션에 연결된 쿠폰은 삭제할 수 없습니다.`)) {
      deleteMutation.mutate(coupon.id);
    }
  };

  const coupons = couponsData?.coupons || [];
  const total = couponsData?.total || 0;

  const couponTypeLabels: Record<string, string> = {
    payment_discount: '결제할인',
    delivery_discount: '배송비할인',
    car_seat_free: '카시트무료',
    insurance_discount: '보험할인',
    other: '기타',
  };

  // ESC 키로 모달 닫기
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showCreateModal) {
        setShowCreateModal(false);
        setEditingCoupon(null);
        resetForm();
      }
    };

    if (showCreateModal) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showCreateModal]);

  return (
    <Layout>
      <div className="px-2 py-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-semibold text-gray-900">쿠폰 관리</h1>
          <button
            onClick={() => {
              resetForm();
              setEditingCoupon(null);
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700"
          >
            + 쿠폰 생성
          </button>
        </div>

        {/* Search Form */}
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-3 mb-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                검색어
              </label>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                placeholder="쿠폰 코드, 이름"
              />
            </div>

            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                쿠폰 종류
              </label>
              <select
                value={couponType}
                onChange={(e) => {
                  setCouponType(e.target.value);
                  setPage(1);
                }}
                className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              >
                <option value="">전체</option>
                <option value="payment_discount">결제할인</option>
                <option value="delivery_discount">배송비할인</option>
                <option value="car_seat_free">카시트무료</option>
                <option value="insurance_discount">보험할인</option>
                <option value="other">기타</option>
              </select>
            </div>

            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                활성 상태
              </label>
              <select
                value={isActive === null ? '' : isActive.toString()}
                onChange={(e) => {
                  setIsActive(e.target.value === '' ? null : e.target.value === 'true');
                  setPage(1);
                }}
                className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              >
                <option value="">전체</option>
                <option value="true">활성</option>
                <option value="false">비활성</option>
              </select>
            </div>

            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700"
            >
              조회
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300"
            >
              초기화
            </button>
          </div>
        </div>

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              쿠폰 목록
              {total > 0 && <span className="ml-2 text-sm font-normal text-gray-600">({total.toLocaleString()}건)</span>}
            </h2>
          </div>

          {isLoading ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
              <p className="mt-3 text-slate-600 text-sm">로딩 중...</p>
            </div>
          ) : error ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <p className="text-rose-600 text-sm">쿠폰 조회에 실패했습니다.</p>
            </div>
          ) : coupons.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <p className="text-slate-500 text-sm">쿠폰이 없습니다.</p>
            </div>
          ) : (
            <>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">코드</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">이름</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">종류</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">할인</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">최소금액</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">상태</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">생성일</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">액션</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {coupons.map((coupon) => (
                      <tr key={coupon.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {coupon.code}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {coupon.name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {couponTypeLabels[coupon.coupon_type]}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {coupon.discount_type === 'fixed'
                            ? `${Math.floor(Number(coupon.discount_value)).toLocaleString()}원`
                            : `${Number(coupon.discount_value)}%`}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {coupon.min_order_amount ? `${coupon.min_order_amount.toLocaleString()}원` : '없음'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            coupon.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {coupon.is_active ? '활성' : '비활성'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {new Date(coupon.created_at).toLocaleDateString('ko-KR')}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <div className="flex gap-1.5">
                            <button
                              onClick={() => handleEdit(coupon)}
                              className="inline-flex items-center justify-center h-8 px-2.5 rounded-md border bg-white shadow-sm hover:bg-gray-50 transition-colors"
                              title="수정"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            </button>
                            <button
                              onClick={() => handleToggleActive(coupon)}
                              className="inline-flex items-center justify-center h-8 px-2.5 rounded-md border bg-white shadow-sm hover:bg-gray-50 transition-colors"
                              title={coupon.is_active ? '비활성화' : '활성화'}
                            >
                              {coupon.is_active ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                                </svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600">
                                  <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={() => handleDelete(coupon)}
                              className="inline-flex items-center justify-center h-8 px-2.5 rounded-md border bg-white shadow-sm hover:bg-gray-50 transition-colors"
                              title="삭제"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600">
                    페이지 <span className="font-medium text-gray-900">{page}</span> / {Math.ceil(total / limit)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    이전
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={coupons.length < limit}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    다음
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h2 className="text-base font-semibold text-gray-900">
                  {editingCoupon ? '쿠폰 수정' : '쿠폰 생성'}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingCoupon(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4">
                <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      쿠폰 코드 *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                      disabled={!!editingCoupon}
                      placeholder="예: WELCOME2024"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      쿠폰 이름 *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                      placeholder="예: 신규 회원 환영 쿠폰"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    설명
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                    placeholder="쿠폰에 대한 상세 설명"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      쿠폰 종류 *
                    </label>
                    <select
                      required
                      value={formData.coupon_type}
                      onChange={(e) => setFormData({ ...formData, coupon_type: e.target.value })}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                    >
                      <option value="payment_discount">결제할인</option>
                      <option value="delivery_discount">배송비할인</option>
                      <option value="car_seat_free">카시트무료</option>
                      <option value="insurance_discount">보험할인</option>
                      <option value="other">기타</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      할인 유형 *
                    </label>
                    <select
                      required
                      value={formData.discount_type}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setFormData({
                          ...formData,
                          discount_type: newType,
                          discount_value: '' // 할인 타입 변경 시 할인 값 초기화
                        });
                      }}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                    >
                      <option value="fixed">정액</option>
                      <option value="percentage">퍼센트</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      할인 {formData.discount_type === 'fixed' ? '금액' : '율'} *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.discount_type === 'fixed' && formData.discount_value
                        ? parseInt(formData.discount_value || '0').toLocaleString()
                        : formData.discount_value}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d.]/g, '');
                        if (formData.discount_type === 'fixed') {
                          // 정액은 정수만
                          const intValue = value.replace(/\./g, '');
                          setFormData({ ...formData, discount_value: intValue });
                        } else {
                          // 퍼센트는 소수점 허용
                          setFormData({ ...formData, discount_value: value });
                        }
                      }}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                      placeholder={formData.discount_type === 'fixed' ? '10,000' : '10'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      최대 할인 금액
                    </label>
                    <input
                      type="text"
                      value={formData.max_discount_amount
                        ? parseInt(formData.max_discount_amount || '0').toLocaleString()
                        : ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d]/g, '');
                        setFormData({ ...formData, max_discount_amount: value });
                      }}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                      placeholder="50,000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      최소 주문 금액
                    </label>
                    <input
                      type="text"
                      value={formData.min_order_amount
                        ? parseInt(formData.min_order_amount || '0').toLocaleString()
                        : ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d]/g, '');
                        setFormData({ ...formData, min_order_amount: value });
                      }}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                      placeholder="100,000"
                    />
                  </div>
                </div>
                </div>

                <div className="sticky bottom-0 bg-white px-4 py-3 border-t border-gray-200 flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingCoupon(null);
                      resetForm();
                    }}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
                  >
                    {editingCoupon ? '수정' : '생성'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
